/**
 * @file deliveryMonth.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description A delivery month — 'YYYY-MM' — and the days it covers (Cam,
 *              28 Oct 2026: "Rep view should be filtered to one month at a
 *              time, and should default to the current month (they can change
 *              to view history or the following month if they wish to)").
 *              Pure, calendar arithmetic only (no clock, no time zone): the
 *              caller says which month "now" is (London today on the server,
 *              the server's `today` on the screen).
 *
 *   The server's utils/deliveryMonth.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const pad = (n) => String(n).padStart(2, '0');

/** 'YYYY-MM' when `v` is one (year 2000–2099), else null. */
export function cleanMonth(v) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(v == null ? '' : v).trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  if (y < 2000 || y > 2099 || mo < 1 || mo > 12) return null;
  return `${m[1]}-${m[2]}`;
}

/** The month a 'YYYY-MM-DD' day (or a longer ISO string) falls in, or null. */
export function monthOfDay(day) {
  const m = /^(\d{4})-(\d{2})-\d{2}/.exec(String(day == null ? '' : day));
  return m ? cleanMonth(`${m[1]}-${m[2]}`) : null;
}

/** The first and last day of a month: { from: 'YYYY-MM-01', to: 'YYYY-MM-30' }, or null. */
export function monthRange(month) {
  const m = cleanMonth(month);
  if (!m) return null;
  const y = Number(m.slice(0, 4));
  const mo = Number(m.slice(5, 7));
  const last = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  return { from: `${m}-01`, to: `${m}-${pad(last)}` };
}

/** The month `n` months after (before, when negative) `month`. */
export function shiftMonth(month, n) {
  const m = cleanMonth(month);
  if (!m) return null;
  const total = Number(m.slice(0, 4)) * 12 + (Number(m.slice(5, 7)) - 1) + Math.trunc(Number(n) || 0);
  return `${Math.floor(total / 12)}-${pad((total % 12) + 1)}`;
}

/** "October 2026". */
export function monthLabel(month) {
  const m = cleanMonth(month);
  if (!m) return '';
  return `${MONTH_NAMES[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
}

/** "Oct 2026". */
export function monthShortLabel(month) {
  const m = cleanMonth(month);
  if (!m) return '';
  return `${MONTH_NAMES[Number(m.slice(5, 7)) - 1].slice(0, 3)} ${m.slice(0, 4)}`;
}

/** Is this day ('YYYY-MM-DD') in that month? */
export function dayInMonth(day, month) {
  const m = cleanMonth(month);
  return !!m && monthOfDay(day) === m;
}

/**
 * The months a picker offers: every month in `extra` (months with something
 * in them), plus `before` months before and `after` months after `around`,
 * newest first, each once.
 */
export function monthChoices(around, { before = 2, after = 2, extra = [] } = {}) {
  const set = new Set();
  const centre = cleanMonth(around);
  if (centre) {
    for (let i = -before; i <= after; i++) set.add(shiftMonth(centre, i));
  }
  for (const e of extra || []) {
    const m = cleanMonth(e);
    if (m) set.add(m);
  }
  return [...set].sort().reverse();
}
